import { useCreate, useList, useOne, useUpdate } from '@refinedev/core';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { enviarArquivo, pedirCredencial } from '../lib/envioDeVideo';
import { ui } from '../ui';
import { formatarData, situacao } from './SeriesList';

/**
 * Uma série: temporadas e episódios.
 *
 * É a tela em que o catálogo do lançamento vai ser carregado — o critério de aceite da
 * história 24 é "subir uma temporada inteira em menos de 30 minutos". Por isso **criar dez
 * episódios é um botão**, não dez formulários: a repetição é o trabalho, e o trabalho é que
 * precisa ser rápido.
 *
 * O envio do vídeo vai **direto do navegador para o Bunny**, com assinatura pedida à Edge
 * Function `bunny-upload` (ver `lib/envioDeVideo.ts`). Terminar o envio não publica o
 * episódio: o Bunny processa e avisa o `bunny-webhook`, que é quem vira o `status` para
 * `ready`. A tela diz isso em vez de deixar a pessoa achando que acabou.
 */
type Temporada = { id: string; number: number; published_at: string | null };

type Episodio = {
  id: string;
  season_id: string;
  number: number;
  duration_s: number;
  teaser: string | null;
  status: 'processing' | 'ready' | 'failed';
  published_at: string | null;
};

const ROTULO_STATUS: Record<Episodio['status'], { texto: string; estilo: object }> = {
  ready: { texto: 'Pronto', estilo: ui.seloOk },
  processing: { texto: 'Processando', estilo: ui.seloEspera },
  failed: { texto: 'Falhou', estilo: { background: 'rgba(229,72,77,.16)', color: '#ff6b6b' } },
};

export function SerieDetalhe() {
  const { id } = useParams<{ id: string }>();
  const [temporadaAberta, setTemporadaAberta] = useState<string | null>(null);

  const { result: serie } = useOne<{ id: string; title: string; slug: string }>({
    resource: 'series',
    id: id ?? '',
  });

  const { query: qTemporadas, result: rTemporadas } = useList<Temporada>({
    resource: 'seasons',
    filters: [{ field: 'series_id', operator: 'eq', value: id }],
    sorters: [{ field: 'number', order: 'asc' }],
  });

  const temporadas = rTemporadas.data;
  const temporadaAtual = temporadaAberta ?? temporadas[0]?.id ?? null;

  const { query: qEpisodios, result: rEpisodios } = useList<Episodio>({
    resource: 'episodes',
    filters: [{ field: 'season_id', operator: 'eq', value: temporadaAtual ?? '' }],
    sorters: [{ field: 'number', order: 'asc' }],
    pagination: { pageSize: 100 },
    queryOptions: { enabled: Boolean(temporadaAtual) },
  });

  const episodios = rEpisodios.data;

  const { mutate: criar } = useCreate();
  const { mutate: atualizar } = useUpdate();

  function novaTemporada() {
    const proxima = (temporadas[temporadas.length - 1]?.number ?? 0) + 1;
    criar(
      { resource: 'seasons', values: { series_id: id, number: proxima, published_at: null } },
      { onSuccess: () => qTemporadas.refetch() },
    );
  }

  /**
   * Cria os episódios que faltam até dez, de uma vez.
   *
   * É o atalho que faz a história 24 caber nos 30 minutos: a pessoa cria a temporada, clica
   * aqui, e depois só preenche gancho e vídeo de cada um.
   */
  function completarEpisodios() {
    if (!temporadaAtual) return;

    const existentes = new Set(episodios.map((e) => e.number));
    const faltando = Array.from({ length: 10 }, (_, i) => i + 1).filter((n) => !existentes.has(n));

    let pendentes = faltando.length;
    if (pendentes === 0) return;

    for (const n of faltando) {
      criar(
        {
          resource: 'episodes',
          values: { season_id: temporadaAtual, number: n, duration_s: 40, status: 'processing' },
          successNotification: false,
        },
        {
          onSettled: () => {
            pendentes -= 1;
            if (pendentes === 0) void qEpisodios.refetch();
          },
        },
      );
    }
  }

  /** Devolve promessa para a linha poder dizer "salvando" e depois "salvo". */
  function salvarGancho(episodio: Episodio, teaser: string): Promise<void> {
    return new Promise((resolve, reject) => {
      atualizar(
        {
          resource: 'episodes',
          id: episodio.id,
          values: { teaser: teaser || null },
          successNotification: false,
        },
        { onSuccess: () => resolve(), onError: (e) => reject(e) },
      );
    });
  }

  function publicarTemporada(temporada: Temporada) {
    atualizar(
      { resource: 'seasons', id: temporada.id, values: { published_at: new Date().toISOString() } },
      { onSuccess: () => qTemporadas.refetch() },
    );
  }

  if (!serie) return <p style={ui.dim}>Carregando…</p>;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <h1 style={ui.h1}>{serie.title}</h1>
        <Link
          to={`/series/${serie.id}/editar`}
          style={{ ...ui.botaoSecundario, marginLeft: 'auto', lineHeight: '40px' }}
        >
          Editar série
        </Link>
      </div>

      <div style={ui.aviso}>
        <strong>Como o vídeo entra.</strong> Escolha o arquivo na linha do episódio: ele vai direto
        do seu navegador para o Bunny. Quando o envio terminar, o episódio fica{' '}
        <strong>Processando</strong> — é o Bunny cortando as qualidades e gerando a legenda. Ele
        vira <strong>Pronto</strong> sozinho, e só então aparece no site. Dá para montar a temporada
        inteira antes (números, ganchos, agendamento) e enviar os vídeos depois, sem refazer nada.
      </div>

      <h2 style={ui.h2}>Temporadas</h2>

      <div style={{ ...ui.linhaBotoes, flexWrap: 'wrap', marginBottom: 16 }}>
        {temporadas.map((t) => {
          const ativa = t.id === temporadaAtual;
          const st = situacao(t.published_at);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTemporadaAberta(t.id)}
              style={ativa ? ui.botao : ui.botaoSecundario}
            >
              Temporada {t.number} · {st.rotulo}
            </button>
          );
        })}
        <button type="button" onClick={novaTemporada} style={ui.botaoSecundario}>
          + Temporada
        </button>
      </div>

      {temporadas.length === 0 && !qTemporadas.isLoading && (
        <p style={ui.dim}>Nenhuma temporada ainda. Crie a primeira acima.</p>
      )}

      {temporadaAtual && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={ui.h2}>Episódios</h2>
            <div style={{ marginLeft: 'auto', ...ui.linhaBotoes }}>
              <button type="button" onClick={completarEpisodios} style={ui.botaoSecundario}>
                Completar até 10
              </button>
              {(() => {
                const t = temporadas.find((x) => x.id === temporadaAtual);
                if (!t || t.published_at) return null;
                return (
                  <button type="button" onClick={() => publicarTemporada(t)} style={ui.botao}>
                    Publicar temporada
                  </button>
                );
              })()}
            </div>
          </div>

          {qEpisodios.isLoading && <p style={ui.dim}>Carregando…</p>}

          {!qEpisodios.isLoading && episodios.length === 0 && (
            <p style={ui.dim}>
              Nenhum episódio. Use &quot;Completar até 10&quot; para criar a temporada inteira de
              uma vez.
            </p>
          )}

          {episodios.length > 0 && (
            <table style={ui.tabela}>
              <thead>
                <tr>
                  <th style={{ ...ui.th, width: 60 }}>Nº</th>
                  <th style={ui.th}>Gancho do fim</th>
                  <th style={{ ...ui.th, width: 230 }}>Vídeo</th>
                  <th style={{ ...ui.th, width: 120 }}>Publicação</th>
                </tr>
              </thead>
              <tbody>
                {episodios.map((ep) => (
                  <LinhaEpisodio
                    key={ep.id}
                    episodio={ep}
                    serie={serie.title}
                    onSalvarGancho={salvarGancho}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}

/**
 * Uma linha de episódio, com o gancho editável no lugar.
 *
 * Abrir um formulário por episódio seria dez navegações para dez frases. O gancho é o único
 * campo que muda com frequência nessa fase, então ele é editado onde está.
 */
function LinhaEpisodio({
  episodio,
  serie,
  onSalvarGancho,
}: {
  episodio: Episodio;
  serie: string;
  onSalvarGancho: (ep: Episodio, teaser: string) => Promise<void>;
}) {
  const [texto, setTexto] = useState(episodio.teaser ?? '');
  const [salvo, setSalvo] = useState(episodio.teaser ?? '');
  const [estado, setEstado] = useState<'parado' | 'salvando' | 'salvo' | 'erro'>('parado');

  const sujo = texto !== salvo;
  const st = ROTULO_STATUS[episodio.status];

  async function salvar() {
    if (!sujo || estado === 'salvando') return;

    setEstado('salvando');
    try {
      await onSalvarGancho(episodio, texto);
      setSalvo(texto);
      setEstado('salvo');
    } catch {
      setEstado('erro');
    }
  }

  return (
    <tr>
      <td style={ui.td}>{episodio.number}</td>
      <td style={ui.td}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              if (estado !== 'parado') setEstado('parado');
            }}
            onBlur={salvar}
            placeholder="Cenas do próximo episódio…"
            style={ui.campo}
            aria-label={`Gancho do episódio ${episodio.number}`}
          />

          {sujo && (
            <button type="button" onClick={salvar} style={ui.botaoSecundario}>
              Salvar
            </button>
          )}

          {/*
            Dizer que gravou não é enfeite. Sem isto, quem digita dez ganchos e sai da página
            não sabe se algum se perdeu — e o salvamento acontece ao sair do campo, que é
            justamente quando a pessoa já está olhando para outro lugar. O teste de ponta a
            ponta também espera por este aviso em vez de adivinhar um tempo.
          */}
          <span
            aria-live="polite"
            style={{ fontSize: 12, minWidth: 72, ...ui.dim }}
            data-estado={estado}
          >
            {estado === 'salvando' && 'Salvando…'}
            {estado === 'salvo' && 'Salvo'}
            {estado === 'erro' && <span style={{ color: '#ff6b6b' }}>Não salvou</span>}
          </span>
        </div>
      </td>
      <td style={ui.td}>
        <EnvioDeVideo episodio={episodio} serie={serie} selo={st} />
      </td>
      <td style={{ ...ui.td, ...ui.dim }}>{formatarData(episodio.published_at)}</td>
    </tr>
  );
}

/**
 * A célula do vídeo: selo de situação e, quando faz sentido, o envio.
 *
 * Três decisões de interface, todas pela mesma razão — quem carrega o catálogo vai repetir
 * isto dezenas de vezes num mesmo dia:
 *
 * 1. **O botão é o próprio `<label>` do campo de arquivo.** Sem diálogo intermediário: um
 *    clique abre o seletor, escolher começa o envio.
 * 2. **A porcentagem é texto, não só barra.** Em envio de vídeo, parar de subir e subir devagar
 *    parecem a mesma coisa numa barra; com número, não parecem.
 * 3. **Terminar o envio não diz "pronto".** Diz "enviado, processando no Bunny", porque é o que
 *    aconteceu. O `status` muda quando o webhook chegar — e aí o selo muda sozinho na próxima
 *    carga da lista.
 */
function EnvioDeVideo({
  episodio,
  serie,
  selo,
}: {
  episodio: Episodio;
  serie: string;
  selo: { texto: string; estilo: object };
}) {
  const [estado, setEstado] = useState<'parado' | 'preparando' | 'enviando' | 'enviado' | 'erro'>(
    'parado',
  );
  const [fracao, setFracao] = useState(0);
  const [erro, setErro] = useState('');

  async function escolheu(arquivo: File | undefined) {
    if (!arquivo) return;

    const titulo = `${serie} · E${episodio.number}`;

    setErro('');
    setFracao(0);
    setEstado('preparando');

    try {
      const credencial = await pedirCredencial(episodio.id, titulo);

      setEstado('enviando');
      await enviarArquivo({ arquivo, credencial, titulo, onProgresso: setFracao });

      setEstado('enviado');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falhou o envio.');
      setEstado('erro');
    }
  }

  const ocupado = estado === 'preparando' || estado === 'enviando';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ ...ui.selo, ...selo.estilo, alignSelf: 'flex-start' }}>{selo.texto}</span>

      {!ocupado && (
        <label
          style={{
            ...ui.botaoSecundario,
            lineHeight: '28px',
            padding: '0 10px',
            fontSize: 12,
            alignSelf: 'flex-start',
            cursor: 'pointer',
          }}
        >
          {episodio.status === 'ready' ? 'Substituir vídeo' : 'Enviar vídeo'}
          <input
            type="file"
            accept="video/*"
            onChange={(e) => void escolheu(e.target.files?.[0])}
            style={{ display: 'none' }}
            aria-label={`Vídeo do episódio ${episodio.number}`}
          />
        </label>
      )}

      {/* `aria-live` porque o envio termina sem ninguém clicar em nada. */}
      <span aria-live="polite" style={{ fontSize: 12, ...ui.dim }} data-envio={estado}>
        {estado === 'preparando' && 'Preparando…'}
        {estado === 'enviando' && `Enviando ${Math.round(fracao * 100)}%`}
        {estado === 'enviado' && 'Enviado. Processando no Bunny.'}
        {estado === 'erro' && <span style={{ color: '#ff6b6b' }}>{erro}</span>}
      </span>
    </div>
  );
}
